import * as types from './actionTypes';
import { setSnackBar, openErrorSnackBar, openSuccessSnackBar } from './snackBarActions';
import { SnackBarProps } from '../../Utils/SnackBarUtils';
import axios from 'axios';
import { openBackdropOverlay, closeBackDropOverlay } from './apiStatusActions';
import { setSubmittedAssetsInformation } from './submittedActions';
import { assetsUrlPath, bulkSearchUrl } from '../../Constants/Api';
import { createAssetsErrorMsg } from '../../Constants/ErrorMessages';
import { logAndReturnErrorMsg } from '../../Utils/ErrorLog';
import { setActiveStep } from './stepperActions';
import { productJsonProps } from '../../Constants/Products';
import { Asset } from '../../Utils/AssetUtils';
import { get, orderBy } from 'lodash';
import _ from 'lodash';
import ShallowCopyFileObject from '../../Utils/FileUtils';
import { getProductPdpUrl } from '../../Utils/ProductUtils';
import Papa from 'papaparse';
import { csvUploadMissingProducts } from './missingProductsActions';

export function dispatchActionData(type, data) {
	return { type: type, data: data };
}
export function addAssets(data) {
	return async function (dispatch) {
		try {
			await dispatch(dispatchActionData(types.ADD_ASSETS_DATA, data));
			const assetsCount = data.length;
			// asset added snack bar success alert should not be displayed, when asset is added by UI automatically on manual entry page.
			if (assetsCount === 1 && data[0].isAddedBySystem === true) {
				return;
			} else {
				dispatch(openSuccessSnackBar(`Added ${assetsCount} Asset(s). `));
			}
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, `Error adding Asset(s)`)));
		}
	};
}
export function updateAssetsData(data) {
	return function (dispatch) {
		dispatch(dispatchActionData(types.UPDATE_ASSETS_DATA, data));
	};
}

export function removeAssets(data) {
	return function (dispatch) {
		dispatch(dispatchActionData(types.REMOVE_ASSETS_DATA, data));
	};
}

export function addAssetFiles(data) {
	return function (dispatch) {
		dispatch(dispatchActionData(types.ADD_ASSET_FILES, data));
	};
}

// Staging a product to an asset.
export function linkProductToAsset(asset, product) {
	return async function (dispatch) {
		const updatedAsset = {
			...asset,
			product: { ...product },
		};
		const productName = get(product, productJsonProps.name);
		try {
			await dispatch(updateAssetsData(updatedAsset));
			dispatch(openSuccessSnackBar(`Product ${productName} linked to asset`));
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, `Error Adding Product ${productName}`)));
		}
	};
}
// un-Staging a product to an asset.
export function unlinkProductFromAsset(asset) {
	return async function (dispatch) {
		const updatedAsset = {
			...asset,
			product: {},
		};
		try {
			await dispatch(updateAssetsData(updatedAsset));
			dispatch(openSuccessSnackBar(`Product unlinked from asset ${asset.name}`));
		} catch (error) {
			dispatch(
				openErrorSnackBar(logAndReturnErrorMsg(error, `Error Unlinking Product from asset ${asset.name}`)),
			);
		}
	};
}
export function setModelFileAsPrimary(data) {
	const { fileName, asset } = data;
	return async function (dispatch) {
		try {
			// set new file as primary. and remove previous file as primary. only one source file can be primary for an asset.
			const modifiedSrcFiles = asset.sourceFiles.map((file) => {
				const shallowFileObj = ShallowCopyFileObject(file);
				if (file.name === fileName) {
					shallowFileObj.isPrimary = true;
				} else {
					shallowFileObj.isPrimary = false;
				}
				return shallowFileObj;
			});
			const updatedAsset = {
				...asset,
				sourceFiles: modifiedSrcFiles,
			};
			await dispatch(updateAssetsData(updatedAsset));
			const snackBarObj = new SnackBarProps(
				`File ${fileName} is set As Primary for asset ${asset.name}`,
				'success',
			);
			dispatch(setSnackBar(snackBarObj));
		} catch (error) {
			console.log(`error setting file ${fileName} as Primary for asset ${asset.name}`, error.response);
		}
	};
}
export function removeAssetFile(data) {
	return async function (dispatch) {
		try {
			await dispatch(dispatchActionData(types.REMOVE_ASSET_FILE, data));
			const message = 'File ' + data.file.name + ' removed from ' + data.assetName + ' asset ';
			const snackBarObj = new SnackBarProps(message, 'success');
			dispatch(setSnackBar(snackBarObj));
		} catch (error) {
			console.log('error removing file from asset', error.response);
		}
	};
}
export function applySelectedTagToAllAssets(data) {
	return async function (dispatch) {
		try {
			await dispatch(dispatchActionData(types.APPLY_SELECTEDTAG_TO_ALLASSETS, data));
			dispatch(openSuccessSnackBar('Tag applied to all assets'));
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, 'Error applying tag to all assets ')));
		}
	};
}
export function removeTagFromAllAssets(data) {
	return async function (dispatch) {
		try {
			await dispatch(dispatchActionData(types.REMOVE_TAG_FROM_ALL_ASSETS, data));
			dispatch(openSuccessSnackBar('Tag removed from all assets'));
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, 'Error removing tag from all assets')));
		}
	};
}

export function removeTagsFromAllAssets(data) {
	return async function (dispatch) {
		try {
			await dispatch(dispatchActionData(types.REMOVE_TAGS_FROM_ALL_ASSETS, data));
			dispatch(openSuccessSnackBar('All Tags removed'));
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, 'Error removing tags from all assets')));
		}
	};
}

//Resetting asset state data
export function resetAssetData() {
	return { type: types.RESET_ASSET_DATA };
}
// Set Create JIRA ticket
export function setCreateJiraTicket(canCreateJiraTicket) {
	return function (dispatch) {
		dispatch(dispatchActionData(types.TOGGLE_CREATE_JIRA_TICKET, canCreateJiraTicket));
		// setting jiraTicket field for each asset in create-asset workflow.
		dispatch(dispatchActionData(types.ASSETS_JIRA_TICKET, canCreateJiraTicket));
	};
}
export function createPostReqBody(assets) {
	let postReqBody = [];
	for (const asset of assets) {
		const assetObj = {
			name: asset.name,
			barcode: asset.barcode,
			tags: asset.tags.map((tag) => tag),
			createJira: asset.createJira,
			productId: asset?.product?.product?.id,
			archived: false,
		};
		postReqBody.push(assetObj);
	}
	return postReqBody;
}
//Post data to Create_Asset endpoint
export function createCustom3dAssets(data, stepperActiveStep, createJira) {
	return async function (dispatch) {
		dispatch(openBackdropOverlay('Creating 3D Assets...'));
		const assetCreateList = createPostReqBody(data);
		try {
			// sending post request to asset post endpoint.
			console.log('creating assets', assetCreateList);
			const response = await axios.post(assetsUrlPath, assetCreateList);
			const createdAssets = response.data;
			console.log('created assets', createdAssets);
			// uploading files after assets are created successfully.
			for (const asset of data) {
				const assetId = createdAssets?.find((a) => a.name === asset.name)?.id;
				if (assetId) {
					for (let index = 0; index < asset.sourceFiles.length; index++) {
						// adding 1 sec delay between each file upload. to avoid race condition (multiple files are set as Primary).
						setTimeout(function () {
							const srcFile = asset.sourceFiles[index];
							const formData = new FormData();
							formData.append('file', srcFile);
							const res = axios.post(`${assetsUrlPath}/${assetId}/files`, formData, {
								params: {
									isPrimary: srcFile.isPrimary,
									assetId: assetId,
								},
								headers: {
									'Content-Type': 'multipart/form-data',
								},
							});
							console.log('file uploaded', res.data);
						}, 1000 * (index + 1));
					}
				}
			}
			dispatch(openBackdropOverlay('Uploading File(s) and Generating artifacts...'));
			// load next page (created assets display page)
			dispatch(setActiveStep(stepperActiveStep + 1));
			dispatch(setSubmittedAssetsInformation(createdAssets));
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, createAssetsErrorMsg)));
		} finally {
			dispatch(closeBackDropOverlay());
		}
	};
}

// Fetch Asset by UUID
export function fetchAssetByUUID(id) {
	// TODO: remove below postman URL
	return axios
		.get(assetsUrlPath + '/' + id)
		.then(function (response) {
			return response;
		})
		.catch((error) => {
			throw error;
		});
}

function sanitizeName(name) {
	if (!name) {
		return '';
	}
	return name
		.replaceAll('$', '')
		.replaceAll('#', '')
		.replaceAll('!', '')
		.replaceAll('/', ' ')
		.replaceAll('\\', ' ')
		.trim();
}

export function processCSVData(bulkSearchReqList) {
	return async function (dispatch) {
		dispatch(openBackdropOverlay('Parsing CSV Data...'));
		try {
			console.log('sending CSV data', bulkSearchReqList);
			const response = await axios.post(bulkSearchUrl, bulkSearchReqList);
			const assets = [];
			if (response.data) {
				const bulkSearchResData = response.data;
				// get the IVM's of missing products
				const missingProducts = bulkSearchReqList.filter((p) => {
					const ivm = p.item + '-' + p.vendor + '-' + p.model;
					return !_.find(bulkSearchResData, (r) => {
						const findIvm = `${r.product.itemNumber}-${r.product.vendorNumber}-${r.product.modelId}`;
						return findIvm.toLowerCase() === ivm.toLowerCase();
					});
				});
				if (missingProducts.length > 0) {
					dispatch(csvUploadMissingProducts(missingProducts));
				}
				dispatch(openErrorSnackBar("there are some missing products IVM's"));
				_.forEach(bulkSearchResData, (item, index) => {
					const product = { product: item?.product };
					let asset = new Asset(index);
					// setting jiraTicket field for each asset in create-asset workflow.
					asset.createJira = true;
					const productName = get(product, productJsonProps.name);
					let assetName = sanitizeName(productName);
					const productId = get(product, productJsonProps.productId) ?? '';
					if (productId) {
						assetName = sanitizeName(`${productId} ${productName}`);
					}
					if (product) {
						asset.product = { ...product };
						if (productName) {
							assetName = sanitizeName(productName);
						}
					}
					// check if there is an existing asset for this product regardless of name.
					if (item.assets && item.assets.length > 0) {
						const ass = item.assets[0];
						asset.existingAssetUUID = ass.id;
						asset.isAssetNameUniqueInBackEnd = false;
						asset.archived = ass.archived;
					}

					asset.name = assetName;
					assets.push(asset);
				});
				// sorting by below asset fields so that assets which has duplicate warnings appear top on the UI asset container.
				assets.sort((a, b) => {
					return (
						a.isAssetNameUnqiueOnUI - b.isAssetNameUnqiueOnUI ||
						a.isAssetNameUniqueInBackEnd - b.isAssetNameUniqueInBackEnd
					);
				});
				await dispatch(addAssets(assets));
				const assetsCount = assets.length;
				dispatch(openSuccessSnackBar(`CSV Uploaded. ${assetsCount} Assets and their information added.`));
			}
		} catch (error) {
			dispatch(openErrorSnackBar(logAndReturnErrorMsg(error, 'error parsing CSV file')));
		} finally {
			dispatch(closeBackDropOverlay());
		}
	};
}
// Export created assets (Upload CSV and Manual Entry workflow) to CSV file.
export function exportCreatedAssetsAsCSV(assets) {
	const fields = ['PPID', 'Model Number', 'Barcode', 'Item Number', 'Product Name', 'URL', 'Product ID'];
	// Sort assets with no JIRA to the top of export csv.
	const sortedAssets = orderBy(assets, ['jiraTask'], ['desc']);
	const data = sortedAssets.map((asset) => {
		const product = asset?.product;
		const PPID = asset.id;
		const modelNumber = get(product, productJsonProps.modelNumber) ?? '';
		const barcode = get(product, productJsonProps.barcode) ?? '';
		const itemNumber = get(product, productJsonProps.itemNumber) ?? '';
		const productName = get(product, productJsonProps.name) ?? '';
		const productUrl = getProductPdpUrl(asset.product) ?? '';
		const productId = get(product, productJsonProps.productId) ?? '';
		return [PPID, modelNumber, barcode, itemNumber, productName, productUrl, productId];
	});
	const csv = Papa.unparse({
		data,
		fields,
	});
	const blob = new Blob([csv]);
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob, { type: 'text/plain' });
	a.download = 'created-assets.csv';
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
}

export function removeInvalidAssets() {
	return async function (dispatch) {
		await dispatch(dispatchActionData(types.DELETE_INVALID_ASSETS));
		dispatch(openSuccessSnackBar('Invalid assets are removed'));
	};
}
